/** People kinds that can be assigned an L1 task. */
export const TASK_ASSIGNEE_KIND_NAMES = new Set([
    'employee',
    'admin',
    'state admin',
    'district admin',
]);
export const PLACEHOLDER_PERSON_EMAIL = 'test@kokomo247.com';
export function normalizeKindName(name) {
    return (name ?? '').trim().toLowerCase().replace(/[-_/]+/g, ' ').replace(/\s+/g, ' ');
}
export function isTaskAssigneeKind(kindName) {
    return TASK_ASSIGNEE_KIND_NAMES.has(normalizeKindName(kindName));
}
export function userIdFromUnknown(value) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return Math.trunc(value);
    }
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0)
            return Math.trunc(parsed);
    }
    return null;
}
export function userIdFromAdditionalFields(fields) {
    if (!fields || typeof fields !== 'object')
        return null;
    return userIdFromUnknown(fields.userId) ?? userIdFromUnknown(fields.user_id);
}
export function displayPersonName(person) {
    const name = [person.firstName, person.lastName]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ');
    return name || person.email?.trim() || (person.id != null ? `User ${person.id}` : 'Unknown');
}
export function defaultTaskRoleType(taskRoles) {
    const preferred = taskRoles.find((role) => /assign|review|task/i.test(role.name ?? ''));
    return preferred?.value ?? taskRoles[0]?.value ?? 0;
}
function emailKey(email) {
    return email?.trim().toLowerCase() ?? '';
}
function isPlaceholderEmail(email) {
    return emailKey(email) === PLACEHOLDER_PERSON_EMAIL;
}
function nameKey(first, last) {
    return [first, last]
        .map((part) => part?.trim().toLowerCase())
        .filter(Boolean)
        .join(' ');
}
function personEmail(person) {
    const direct = person.email?.trim() ?? '';
    if (direct && !isPlaceholderEmail(direct))
        return direct;
    const fromFields = typeof person.additionalFields?.email === 'string'
        ? person.additionalFields.email.trim()
        : '';
    return fromFields && !isPlaceholderEmail(fromFields) ? fromFields : '';
}
/** Store the directory user on the person so later task assign can resolve them. */
export function withPersonUserId(fields, userId) {
    const next = { ...(fields && typeof fields === 'object' ? fields : {}) };
    const resolved = userIdFromUnknown(userId);
    if (resolved) {
        next.userId = resolved;
        return next;
    }
    delete next.userId;
    delete next.user_id;
    return next;
}
/** Resolve a Kokomo user id from a stored id, email, or unique first+last name. */
export function resolvePersonUserId(person, users) {
    const stored = userIdFromUnknown(person.userId) ?? userIdFromAdditionalFields(person.additionalFields);
    if (stored)
        return stored;
    const email = personEmail(person);
    if (email) {
        const match = users.find((user) => emailKey(user.email) === emailKey(email));
        const id = userIdFromUnknown(match?.id);
        if (id)
            return id;
    }
    const personName = nameKey(person.firstName, person.lastName);
    if (!personName)
        return null;
    const matches = users.filter((user) => nameKey(user.firstName, user.lastName) === personName);
    return matches.length === 1 ? userIdFromUnknown(matches[0].id) : null;
}
/** Eligible assignees: staff kinds on the L1, plus created-by and reported-by. */
export function collectTaskAssignees(input) {
    const kindNameByValue = new Map();
    for (const entry of input.personKinds ?? []) {
        if (typeof entry.value === 'number') {
            kindNameByValue.set(entry.value, entry.name?.trim() || '');
        }
    }
    const users = input.users ?? [];
    const userById = new Map();
    for (const user of users) {
        const id = userIdFromUnknown(user.id);
        if (id)
            userById.set(id, user);
    }
    const seen = new Set();
    const assignees = [];
    const add = (assignee) => {
        if (assignee.userId <= 0 || seen.has(assignee.userId))
            return;
        seen.add(assignee.userId);
        assignees.push(assignee);
    };
    for (const person of input.personsInvolved ?? []) {
        const kindName = typeof person.kind === 'number' ? kindNameByValue.get(person.kind) ?? '' : '';
        if (!isTaskAssigneeKind(kindName))
            continue;
        const userId = resolvePersonUserId(person, users);
        if (userId == null)
            continue;
        const lookupUser = userById.get(userId);
        add({
            userId,
            name: displayPersonName({
                firstName: person.firstName || lookupUser?.firstName,
                lastName: person.lastName || lookupUser?.lastName,
                email: person.email || lookupUser?.email,
                id: userId,
            }),
            email: person.email?.trim() || lookupUser?.email?.trim() || undefined,
            source: 'person',
        });
    }
    const addIncidentUser = (user, source) => {
        const userId = userIdFromUnknown(user?.id);
        if (userId == null)
            return;
        const lookupUser = userById.get(userId);
        add({
            userId,
            name: displayPersonName({
                firstName: user?.firstName || lookupUser?.firstName,
                lastName: user?.lastName || lookupUser?.lastName,
                email: user?.email || lookupUser?.email,
                id: userId,
            }),
            email: user?.email?.trim() || lookupUser?.email?.trim() || undefined,
            source,
        });
    };
    addIncidentUser(input.createdBy, 'createdBy');
    addIncidentUser(input.reportBy, 'reportedBy');
    return assignees.sort((a, b) => a.name.localeCompare(b.name));
}
